import { BeakerIcon } from "@heroicons/react/20/solid";

import { MultiButtonCardHeader } from "../../../../../common/Atoms/MultiButtonCardHeader";
import { Notification } from "../../../../../common/Atoms/Notification";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import { useCurrentGroup } from "../../../../hooks/useGroups";
import {
  useCreatePaymentPlanConfigMutation,
  useDeletePaymentPlanConfigMutation,
  useModifyPaymentPlanConfigMutation,
} from "../../../../services/api/groupApi/group";

import { PaymentPlanConfigForm } from "./PaymentPlanConfigForm";

export function PaymentPlanConfigRoot() {
  const [createPaymentPlanConfig, { isLoading: isCreating }] = useCreatePaymentPlanConfigMutation();
  const [deletePaymentPlanConfig, { isLoading: isDeleting }] = useDeletePaymentPlanConfigMutation();
  const [modifyPaymentPlanConfig, { isLoading: isUpdating }] = useModifyPaymentPlanConfigMutation();
  const currentOrg = useGetSelectedOrganisation();
  const { paymentPlanConfigs, groupFormData } = useCurrentGroup();

  function handlePaymentPlanConfigCreate() {
    if (!currentOrg || !groupFormData?.id) {
      return;
    }

    createPaymentPlanConfig({
      organisationId: currentOrg.id,
      groupId: groupFormData.id,
    });
  }

  function handlePaymentPlanConfigSave(updatedConfig) {
    if (!currentOrg || !groupFormData?.id || !updatedConfig?.id) {
      return;
    }

    // Pick only updatable fields
    const update = {
      maxNumberOfPayments: updatedConfig.maxNumberOfPayments,
      frequencyType: updatedConfig.frequencyType,
      firstPaymentDates: updatedConfig.firstPaymentDates,
      surchargeUnit: updatedConfig.surchargeUnit,
      surchargePercent: updatedConfig.surchargePercent ? Number(updatedConfig.surchargePercent) : null,
      surchargeCents: updatedConfig.surchargeCents ? Number(updatedConfig.surchargeCents) : null,
      externalSurchargeAccountCode: updatedConfig.externalSurchargeAccountCode || null,
      minInvoiceDaysOverdue: updatedConfig.minInvoiceDaysOverdue ?? null,
      maxInvoiceDaysOverdue: updatedConfig.maxInvoiceDaysOverdue ?? null,
      brandingThemeFilterType: updatedConfig.brandingThemeFilterType || null,
      brandingThemeIds: updatedConfig.brandingThemeFilterType ? (updatedConfig.brandingThemeIds || []) : null,
    };

    modifyPaymentPlanConfig({
      organisationId: currentOrg.id,
      groupId: groupFormData.id,
      paymentPlanConfigId: updatedConfig.id,
      update,
    });
  }

  function handlePaymentPlanConfigDelete(paymentPlanConfigId: number) {
    if (!currentOrg || !groupFormData?.id || !paymentPlanConfigId) {
      return;
    }

    deletePaymentPlanConfig({
      organisationId: currentOrg.id,
      groupId: groupFormData.id,
      paymentPlanConfigId,
    });
  }

  if (!paymentPlanConfigs) {
    return null;
  }

  const hasCurrentConfig = paymentPlanConfigs.length > 0;

  return (
    <div>
      <MultiButtonCardHeader
        title={ `Payment Plan Configurations` }
        badge={ hasCurrentConfig ? `Enabled` : undefined }
        badgeColor={ `green` }
        badgeTooltip={ hasCurrentConfig ? `Payment Plans are enabled for this group` : undefined }
        description={ `Payment Plans are enabled for this group whenever a configuration exists. Configure eligibility and plan terms for the portal here.` }
        secondaryDescription={ `To disable Payment Plans for this group, delete the configuration below. This does not affect payment plans created by users within Paidnice.` }
        buttons={ [{
          buttonText: `New Payment Plan Configuration`,
          onClick: handlePaymentPlanConfigCreate,
          loading: isCreating,
          color: `paidniceGreen`,
          disabled: hasCurrentConfig || isCreating,
          tooltip: hasCurrentConfig ? `Payment Plans are already enabled for this group. Delete the existing configuration to disable or replace it.` : undefined,
          size: `md`,
        }] }
      />

      {
        paymentPlanConfigs.map(config => {
          return (
            <PaymentPlanConfigForm
              config={ config }
              key={ config.id + `pp-config` }
              onSave={ handlePaymentPlanConfigSave }
              onDelete={ handlePaymentPlanConfigDelete }
              loading={ isDeleting || isUpdating }
            />
          );
        })
      }

      <Notification
        type={ `pro` }
        icon={ BeakerIcon }
        className={ `mt-4` }
      >
        { `Payment Plans are a new feature, we will be adding more functionality in the near future. If you have any feedback or feature requests, please get in touch!` }
      </Notification>
    </div>
  );
}
